import { createContext, useContext, useRef, useState, useCallback, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useStore } from "../../stores/store";
import { useAccessControl } from "../../hooks/useAccessControl";
import { useSquawkAudioPrompts } from "../../hooks/useSquawkAudioPrompts";
import { useGetSquawkConnectionType } from "./useGetSquawkConnectionType";
import { useSetSquawkConnectionType } from "./useSetSquawkConnectionType";
import { useWebRTCPlayer, MAX_RTC_RETRIES } from "./webrtc/useWebRTCPlayer";
import { useMSEPlayer, MAX_MSE_RETRIES } from "./mse/useMSEPlayer";
import { useShoutcastPlayer, MAX_SHOUTCAST_RETRIES } from "./shoutcast/useShoutcastPlayer";
import { useDemoSquawkPlayer } from "./demo/useDemoSquawkPlayer";
import { useBackgroundAudioKeepAlive } from "./mse/useBackgroundAudioKeepAlive";
import { useSquawkV2Player, MAX_V2_RETRIES } from "./v2/useSquawkV2Player";
import { trackFeatureUsage } from "../../utils/gtm";
import { diagnosticEventLog } from "../../shared/services/diagnostic";
import { channels } from "./squawkChannels";
import { PLAYER_PRIORITY } from "./types";

// Create context
const SquawkPlayerContext = createContext(null);

/**
 * SquawkPlayerProvider - Centralized audio player state and connection management
 *
 * This provider:
 * - Maintains a single, persistent audio connection
 * - Never unmounts (stays alive during layout changes)
 * - Provides shared state and methods to multiple UI components
 * - Prevents duplicate connections and echo issues
 */
// eslint-disable-next-line react/prop-types -- legacy .jsx provider; prop typing is handled via TS consumers
export const SquawkPlayerProvider = ({ children }) => {
  const selectedChannel = useStore((s) => s.selectedChannel);
  const setSelectedChannel = useStore((s) => s.setSelectedChannel);

  const { authenticated, isInDemoMode } = useAccessControl();
  const { data: connectionType, isFetched: connectionTypeFetched } =
    useGetSquawkConnectionType();
  const { mutate: setSquawkConnectionType } = useSetSquawkConnectionType();

  const [volume, setVolume] = useState(1);

  // Restore persisted channel on mount. sessionStorage scopes the value to
  // this tab so a Cmd+R reload auto-resumes audio while a fresh tab stays
  // silent. Tabs are intentionally ignorant of each other; if the user opens
  // a duplicate tab, both may end up playing — they can stop one manually.
  useEffect(() => {
    try {
      const persistedName = sessionStorage.getItem("squawk.selectedChannel");
      if (persistedName && !selectedChannel) {
        const channel = channels.find((c) => c.name === persistedName);
        if (channel) {
          diagnosticEventLog.record("squawk:audio", "info", "channel-restore", { channel: persistedName });
          setSelectedChannel(channel);
        } else {
          sessionStorage.removeItem("squawk.selectedChannel");
        }
      }
    } catch {
      // sessionStorage unavailable
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debouncing: Track last channel selection time to prevent rapid switching
  const lastChannelSelectTime = useRef(0);
  const CHANNEL_SELECT_DEBOUNCE_MS = 300;

  // Persistent refs for audio elements - these never unmount
  const rtcElRef = useRef(null);
  const mseElRef = useRef(null);
  const mseKeepAliveElRef = useRef(null);
  const shoutcastElRef = useRef(null);
  const demoElRef = useRef(null);

  // MSE element version. Bumped via the `key` prop to force React to unmount
  // and remount the <video> element when MSEPlayerManager detects WebKit's
  // MSE pipeline is irrecoverably wedged (consecutive no-data errors). A fresh
  // DOM node gives Safari a clean MSE state to attach to.
  // (Sentry Issue NEWSQUAWK-PORTALS-17A escape hatch.)
  const [mseElementVersion, setMseElementVersion] = useState(0);
  const requestFreshMseElement = useCallback(() => {
    diagnosticEventLog.record("squawk:audio", "warn", "mse-element-swap", {
      reason: "consecutive-no-data-errors",
    });
    setMseElementVersion((v) => v + 1);
  }, []);

  // Determine the user's preferred transport straight from the saved connection
  // type (WebRTC is the default for a fresh account). v2 is now a first-class
  // connection type and a normal participant in the fallback chain, not a hard
  // bypass of the v1 players. The backend enum value `web_sockets` maps to the
  // MSE player.
  const preferredPlayer =
    !connectionType || connectionType === "web_rtc"
      ? "webrtc"
      : connectionType === "v2"
      ? "v2"
      : connectionType === "shoutcast"
      ? "shoutcast"
      : "mse";

  // One-time migration: v2 used to be a device-local localStorage opt-in
  // ("squawk.playerV2Enabled") that overrode connectionType. It's now a
  // first-class connection type. If a user still has the legacy flag on, promote
  // them to the account-synced "v2" connection type (only when they have no saved
  // type yet, so we never clobber a deliberate choice) and drop the stale key.
  const v2MigrationDoneRef = useRef(false);
  useEffect(() => {
    if (v2MigrationDoneRef.current || !authenticated || !connectionTypeFetched) return;
    let legacyV2 = false;
    try {
      legacyV2 = localStorage.getItem("squawk.playerV2Enabled") === "true";
    } catch {
      /* localStorage unavailable (private mode / quota) */
    }
    if (!legacyV2) {
      v2MigrationDoneRef.current = true;
      return;
    }
    v2MigrationDoneRef.current = true;
    if (!connectionType) {
      diagnosticEventLog.record("squawk:audio", "info", "v2-pref-migrated", {});
      setSquawkConnectionType("v2");
    }
    try {
      localStorage.removeItem("squawk.playerV2Enabled");
    } catch {
      /* localStorage unavailable (private mode / quota) */
    }
  }, [authenticated, connectionTypeFetched, connectionType, setSquawkConnectionType]);

  // Preventive multi-player protection: Only enable one player at a time
  // When switching connection types, ensure the old player is fully disabled before enabling the new one
  const [enabledPlayer, setEnabledPlayer] = useState(preferredPlayer);

  // Session-scoped fallback override — when a player exhausts retries, auto-fall to the next.
  // Not persisted to user preferences (they chose their connection type for a reason).
  // null = no override, use user preference; "webrtc" | "mse" | "v2" | "shoutcast" = active fallback
  const [fallbackOverride, setFallbackOverride] = useState(null);
  const fallbackAttemptedRef = useRef(new Set());

  // Update enabled player when connection type or fallback changes
  useEffect(() => {
    const targetPlayer = fallbackOverride || preferredPlayer;

    // If switching players, briefly disable all to ensure clean transition
    if (enabledPlayer !== targetPlayer) {
      console.log(`[SquawkPlayerContext] Switching from ${enabledPlayer} to ${targetPlayer}${fallbackOverride ? " (auto-fallback)" : ""}`);
      diagnosticEventLog.record("squawk:audio", "info", "player-switch", { from: enabledPlayer, to: targetPlayer, fallback: !!fallbackOverride });
      // First disable current player
      setEnabledPlayer(null);
      // Then enable new player after a brief delay
      // During this 100ms gap:
      // - All players are disabled (enabled=false)
      // - All players transition to "idle" state
      // - No audio plays (prevents echo during transition)
      // - This is INTENTIONAL and ensures clean handoff
      const timer = setTimeout(() => {
        setEnabledPlayer(targetPlayer);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [preferredPlayer, enabledPlayer, fallbackOverride]);

  // Use WebRTC player hook - only enabled when explicitly set and v2 is not active
  const webRTCPlayer = useWebRTCPlayer({
    audioElement: rtcElRef.current,
    address: selectedChannel?.address || null,
    enabled: enabledPlayer === "webrtc" && !!selectedChannel,
    authenticated,
  });

  // Use MSE player hook - only enabled when explicitly set and v2 is not active
  const msePlayer = useMSEPlayer({
    audioElement: mseElRef.current,
    address: selectedChannel?.address || null,
    enabled: enabledPlayer === "mse" && !!selectedChannel,
    authenticated,
    onRequestFreshElement: requestFreshMseElement,
  });

  // Use Shoutcast player hook - HTTP audio stream, background-safe, disabled when v2 is active
  const shoutcastPlayer = useShoutcastPlayer({
    audioElement: shoutcastElRef.current,
    address: selectedChannel?.address || null,
    enabled: enabledPlayer === "shoutcast" && !!selectedChannel,
    authenticated,
  });

  // Use Demo player hook - for logged-out users and non-subscribers to play historical recordings
  // TODO(demo-playback): Re-enable when backend audio serving is restored.
  // Forcing enabled: false prevents any initialisation even if a channel was
  // persisted from a previous session or a user's state gets into an odd shape.
  const DEMO_PLAYBACK_ENABLED = false;
  const demoPlayer = useDemoSquawkPlayer({
    audioElement: demoElRef.current,
    channel: selectedChannel?.demoChannel || null,
    enabled: DEMO_PLAYBACK_ENABLED && isInDemoMode && !!selectedChannel,
  });

  // Use V2 player hook - WebSocket + MSE streaming via audio-v2 backend
  const v2Player = useSquawkV2Player({
    v2Keyword: selectedChannel?.v2Keyword || null,
    enabled: enabledPlayer === "v2" && !isInDemoMode && !!selectedChannel,
    authenticated,
  });

  // Silent audio keepalive — prevents Chrome from pausing background-tab media
  // ("video-only background media" power-saving). Both the MSE player (<video>)
  // and the v2 player (MSE on a hidden <audio>) need this to keep streaming
  // while the tab is hidden. Only one player is ever active, so a single
  // keepalive element serves whichever is connected.
  useBackgroundAudioKeepAlive({
    audioElement: mseKeepAliveElRef.current,
    active:
      (enabledPlayer === "mse" && msePlayer.status === "connected") ||
      (enabledPlayer === "v2" && v2Player.status === "connected"),
  });

  // Auto-fallback chain — global priority WebRTC → v2 → MSE → Shoutcast (see
  // PLAYER_PRIORITY). From the user's preferred transport we fall through the rest
  // in priority order, so a transport their network blocks degrades onto one that
  // works — a WebRTC user whose UDP is firewalled now reaches v2 before MSE.
  // Session-scoped only — resets on channel deselect/reselect.
  const fallbackChain = useMemo(
    () => [preferredPlayer, ...PLAYER_PRIORITY.filter((p) => p !== preferredPlayer)],
    [preferredPlayer]
  );

  useEffect(() => {
    // v2 is a normal participant in the chain, not a bypass — only demo mode /
    // no channel short-circuits.
    if (!selectedChannel || isInDemoMode) return;

    const idx = fallbackChain.indexOf(enabledPlayer);
    if (idx === -1 || idx >= fallbackChain.length - 1) return; // not in chain, or already at the last resort
    const next = fallbackChain[idx + 1];
    const transitionKey = `${enabledPlayer}→${next}`;
    if (fallbackAttemptedRef.current.has(transitionKey)) return;

    // Resolve the currently-enabled player's status / retry budget.
    const current = {
      webrtc: { status: webRTCPlayer.status, retryCount: webRTCPlayer.retryCount, max: MAX_RTC_RETRIES, error: webRTCPlayer.error },
      mse: { status: msePlayer.status, retryCount: msePlayer.retryCount, max: MAX_MSE_RETRIES, error: msePlayer.error },
      v2: { status: v2Player.status, retryCount: v2Player.retryCount, max: MAX_V2_RETRIES, error: v2Player.error },
      shoutcast: { status: shoutcastPlayer.status, retryCount: shoutcastPlayer.retryCount, max: MAX_SHOUTCAST_RETRIES, error: shoutcastPlayer.error },
    }[enabledPlayer];

    if (current && current.status === "error" && current.retryCount >= current.max) {
      fallbackAttemptedRef.current.add(transitionKey);
      console.warn(`[SquawkPlayerContext] ${enabledPlayer} exhausted retries — auto-fallback to ${next}`);
      diagnosticEventLog.record("squawk:audio", "warn", "auto-fallback", { from: enabledPlayer, to: next });
      Sentry.captureMessage(`Squawk auto-fallback: ${enabledPlayer} → ${next}`, {
        level: "warning",
        tags: { component: "SquawkPlayerContext", context: "auto_fallback", from_player: enabledPlayer, to_player: next },
        extra: { lastError: current.error?.message, retryCount: current.retryCount, channel: selectedChannel?.name },
      });
      setFallbackOverride(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .error?.message is Sentry telemetry only; depending on it would re-fire this fallback effect on every error-message change
  }, [
    enabledPlayer, fallbackChain, selectedChannel, isInDemoMode,
    webRTCPlayer.status, webRTCPlayer.retryCount,
    msePlayer.status, msePlayer.retryCount,
    v2Player.status, v2Player.retryCount,
    shoutcastPlayer.status, shoutcastPlayer.retryCount,
  ]);

  // Safety check: ensure multiple players are never actively connecting/connected simultaneously
  // This should never happen due to the preventive enabledPlayer state,
  // but provides a fail-safe that logs critical errors
  useEffect(() => {
    const activePlayers = [
      webRTCPlayer.status === "connecting" || webRTCPlayer.status === "connected" ? "webrtc" : null,
      msePlayer.status === "connecting" || msePlayer.status === "connected" ? "mse" : null,
      v2Player.status === "connecting" || v2Player.status === "connected" ? "v2" : null,
      shoutcastPlayer.status === "connecting" || shoutcastPlayer.status === "connected" ? "shoutcast" : null,
    ].filter(Boolean);

    if (activePlayers.length > 1) {
      console.error(
        `[SquawkPlayerContext] CRITICAL: Multiple players active simultaneously: ${activePlayers.join(", ")}! ` +
        "This should never happen with preventive checks. Forcing cleanup."
      );
      diagnosticEventLog.record("squawk:audio", "error", "multi-player-critical", {
        webrtcStatus: webRTCPlayer.status,
        mseStatus: msePlayer.status,
        v2Status: v2Player.status,
        shoutcastStatus: shoutcastPlayer.status,
      });
      setSelectedChannel(null);
      setEnabledPlayer(null);
    }
  }, [webRTCPlayer.status, msePlayer.status, v2Player.status, shoutcastPlayer.status, setSelectedChannel]);

  // Persist a successful v2 fallback as the user's default. When the auto-fallback
  // chain (or the "switch to v2 now" prompt) lands the user on v2 and it actually
  // connects, promote v2 to their saved connection type so future sessions start
  // there directly instead of re-flapping through WebRTC first.
  //
  // Two-step to avoid a revert race: (1) write the pref while KEEPING the session
  // override, then (2) once the refetched connectionType has caught up to "v2",
  // drop the now-redundant override. Clearing it in step 1 would briefly resolve
  // targetPlayer back to the old preferred transport (the async pref write hasn't
  // landed yet) and bounce playback off v2. `v2PersistedRef` dedupes the PATCH
  // during the async window; it's reset per channel session in handleChannelSelect.
  const v2PersistedRef = useRef(false);
  useEffect(() => {
    if (
      !v2PersistedRef.current &&
      fallbackOverride === "v2" &&
      v2Player.status === "connected" &&
      connectionType !== "v2"
    ) {
      v2PersistedRef.current = true;
      diagnosticEventLog.record("squawk:audio", "info", "v2-promoted-to-default", { from: preferredPlayer });
      setSquawkConnectionType("v2");
      return;
    }
    // Step 2: saved default has caught up — drop the override (preferredPlayer is
    // already "v2", so playback stays put with no disruptive re-switch).
    if (connectionType === "v2" && fallbackOverride === "v2") {
      setFallbackOverride(null);
    }
  }, [fallbackOverride, v2Player.status, connectionType, preferredPlayer, setSquawkConnectionType]);

  // Session-scoped instant jump to v2, called from the network-issue prompt. Runs
  // inside the user's click gesture. If v2 then connects, the effect above persists
  // it as the default.
  const switchToV2 = useCallback(() => {
    diagnosticEventLog.record("squawk:audio", "info", "manual-switch-to-v2", { from: preferredPlayer });
    setFallbackOverride("v2");
  }, [preferredPlayer]);

  // Get the active player's status. The active player is the current fallback
  // target (if a player exhausted its retries) or the user's preferred transport.
  // During the 100ms switch gap (enabledPlayer=null) the target player reads
  // "idle" — intentional, it prevents echo during handoff. Demo mode overrides all.
  const targetName = fallbackOverride || preferredPlayer;
  const activePlayer = isInDemoMode
    ? demoPlayer
    : targetName === "v2"
    ? v2Player
    : targetName === "webrtc"
    ? webRTCPlayer
    : targetName === "mse"
    ? msePlayer
    : targetName === "shoutcast"
    ? shoutcastPlayer
    : webRTCPlayer;
  const status = activePlayer.status;

  // True while the user's preferred (non-v2) transport is flapping — it has
  // retried at least once but hasn't connected and hasn't yet auto-fallen onto
  // another transport. Consumed by SquawkNetworkIssueBanner to offer an early
  // one-click "switch to v2 now" before the chain gets there on its own. Clears
  // the moment the transport connects OR auto-fallback kicks in (fallbackOverride
  // set). Only meaningful when v2 isn't already the preferred transport.
  const isPrimaryRetrying =
    !!selectedChannel &&
    !isInDemoMode &&
    authenticated &&
    preferredPlayer !== "v2" &&
    fallbackOverride === null &&
    status !== "connected" &&
    (activePlayer.retryCount ?? 0) >= 1;

  // Log status changes for diagnostics
  useEffect(() => {
    diagnosticEventLog.record("squawk:audio", "info", "status-change", { status, enabledPlayer, channel: selectedChannel?.name ?? null });
  }, [status, enabledPlayer, selectedChannel?.name]);

  // Track whether the squawk should be playing but isn't — surfaces the
  // disconnect banner. True when an authenticated, non-demo user has a
  // channel selected and the active player hasn't reached "connected" within
  // DISCONNECT_GRACE_MS (gives a healthy connection time to negotiate before
  // alarming the user). Suppressed in demo mode and for logged-out users
  // because they have no live playback path — the banner would be misleading.
  // On high-MEI domains audio usually resumes well within the grace window,
  // so the banner stays hidden; on low-MEI it surfaces after every reload
  // until the user clicks to re-arm playback with a fresh user gesture.
  const DISCONNECT_GRACE_MS = 3000;
  const [isDisconnectedForBanner, setIsDisconnectedForBanner] = useState(false);
  useEffect(() => {
    if (!selectedChannel || status === "connected" || isInDemoMode || !authenticated) {
      setIsDisconnectedForBanner(false);
      return;
    }
    const timer = setTimeout(() => {
      setIsDisconnectedForBanner(true);
    }, DISCONNECT_GRACE_MS);
    return () => clearTimeout(timer);
  }, [selectedChannel, status, isInDemoMode, authenticated]);

  // Banner-driven reconnect. Called from the SquawkDisconnectedBanner click
  // handler so we run inside fresh user-gesture context — that's exactly
  // what Chrome's autoplay policy requires to let audio.play() through on
  // low-MEI domains. We best-effort call play() on every audio/video
  // element; the one currently attached to a media source (srcObject for
  // WebRTC, src for shoutcast) starts playing, the others reject with a
  // harmless error we swallow. The Flussonic PLAY event then fires from
  // the audio element's 'playing' event, status flips to "connected", and
  // isDisconnectedForBanner clears via the effect above.
  const reconnect = useCallback(() => {
    [rtcElRef, mseElRef, shoutcastElRef, demoElRef].forEach((ref) => {
      const el = ref.current;
      if (!el || !el.paused) return;
      el.play().catch(() => {
        // No source on this element, or play rejected — ignore.
      });
    });
  }, []);

  // Plays audio prompts on status change
  useSquawkAudioPrompts(status, selectedChannel?.name ?? "", { isInDemoMode });

  // Channel selection handler
  const handleChannelSelect = useCallback(
    (channel) => {
      // Check if user is deselecting (stopping) the current channel
      const isDeselecting = selectedChannel?.name === channel.name;

      // Debounce: Prevent rapid channel switching that could cause race conditions
      // CRITICAL: Do NOT debounce deselection - users must be able to stop audio instantly
      if (!isDeselecting) {
        const now = Date.now();
        const timeSinceLastSelect = now - lastChannelSelectTime.current;

        if (timeSinceLastSelect < CHANNEL_SELECT_DEBOUNCE_MS) {
          console.log(`[SquawkPlayerContext] Debouncing channel switch (${timeSinceLastSelect}ms since last select)`);
          diagnosticEventLog.record("squawk:audio", "warn", "channel-debounced", { timeSinceLastMs: timeSinceLastSelect });
          return;
        }

        lastChannelSelectTime.current = now;
      }

      // Prevent spam-clicking to RE-SELECT the same channel while it's already connecting
      // IMPORTANT: This should NOT block deselection - users must always be able to stop audio
      // Since clicking same channel = toggle/deselect, this check should never block when isDeselecting
      if (
        status === "connecting" &&
        selectedChannel?.name === channel.name &&
        !isDeselecting  // CRITICAL: Allow deselection even during connecting!
      ) {
        // This case would only occur if somehow we're trying to "re-select" while connecting
        // which shouldn't happen with toggle logic, but keeping as safeguard
        return;
      }

      // Toggle channel selection
      if (isDeselecting) {
        // Deselect channel - this will trigger cleanup in the hooks
        // This is ALWAYS allowed immediately (no debounce) for good UX
        setSelectedChannel(null);
        setFallbackOverride(null);
        fallbackAttemptedRef.current.clear();
        v2PersistedRef.current = false;
        try { sessionStorage.removeItem("squawk.selectedChannel"); } catch { /* sessionStorage unavailable (private mode / quota) */ }
        diagnosticEventLog.record("squawk:audio", "info", "channel-deselect", { channel: channel.name });
        trackFeatureUsage({ feature_name: "squawk_player", feature_action: "channel_deselect" });
        // Don't force remount here - it causes race conditions with disconnect
        // The existing element will be reused for next connection
      } else {
        // Select new channel - this will trigger connection in the hooks
        setSelectedChannel(channel);
        setFallbackOverride(null);
        fallbackAttemptedRef.current.clear();
        v2PersistedRef.current = false;
        try { sessionStorage.setItem("squawk.selectedChannel", channel.name); } catch { /* sessionStorage unavailable (private mode / quota) */ }
        diagnosticEventLog.record("squawk:audio", "info", "channel-select", { channel: channel.name, connectionType, isDemoMode: isInDemoMode });
        trackFeatureUsage({ feature_name: "squawk_player", feature_action: "channel_select", feature_detail: channel.name });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- connectionType/isInDemoMode are used only for the diagnostic log; excluded to keep this channel handler referentially stable
    [selectedChannel, status, setSelectedChannel]
  );

  // Volume handler - applies to all audio elements directly for stability
  // Uses refs instead of player objects to avoid unnecessary re-creation
  const handleVolumeChange = useCallback(
    (newVolume) => {
      setVolume(newVolume);

      // Set volume directly on audio elements for consistency
      // This prevents issues when switching connection types
      // Using refs makes this callback stable (no dependencies)
      try {
        if (rtcElRef.current) {
          rtcElRef.current.volume = newVolume;
        }
      } catch (e) {
        console.debug("Error setting WebRTC volume:", e);
      }

      try {
        if (mseElRef.current) {
          mseElRef.current.volume = newVolume;
        }
      } catch (e) {
        console.debug("Error setting MSE volume:", e);
      }

      try {
        if (shoutcastElRef.current) {
          shoutcastElRef.current.volume = newVolume;
        }
      } catch (e) {
        console.debug("Error setting Shoutcast volume:", e);
      }

      try {
        if (demoElRef.current) {
          demoElRef.current.volume = newVolume;
        }
      } catch (e) {
        console.debug("Error setting demo volume:", e);
      }

      // V2 player uses GainNode — delegate via hook's setVolume
      try {
        v2Player.setVolume(newVolume);
      } catch (e) {
        console.debug("Error setting V2 volume:", e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depends only on the stable v2Player.setVolume; the whole v2Player object would needlessly recreate handleVolumeChange
    [v2Player.setVolume]
  );

  // Context value
  const value = {
    // State
    selectedChannel,
    status,
    volume,
    connectionType,
    // The user's actual preferred transport as a player name ("webrtc" | "mse" |
    // "v2" | "shoutcast"). Unlike connectionType this also reflects the v2 toggle,
    // so the Troubleshooting fallback notice can name the real preferred transport.
    preferredPlayer,

    // Methods
    handleChannelSelect,
    handleVolumeChange,

    // For debugging
    retryCount: activePlayer.retryCount ?? 0,
    error: activePlayer.error,

    // Session-scoped auto-fallback state. `fallbackOverride` is the transport
    // we were forced onto ("mse" | "shoutcast" | null); `didAutoFallback` is a
    // convenience boolean for UI (the Troubleshooting page surfaces a notice
    // and asks the user to contact support so we can investigate why their
    // preferred transport failed). Resets on channel deselect/reselect.
    fallbackOverride,
    didAutoFallback: fallbackOverride !== null,

    // True while the preferred (non-v2) transport is flapping but hasn't yet
    // auto-fallen — drives the network-issue prompt. `switchToV2` is the prompt's
    // one-click session jump to v2 (persisted as default once v2 connects).
    isPrimaryRetrying,
    switchToV2,

    // Demo mode flag
    isDemoMode: isInDemoMode,

    // True when the user has a channel selected but the active player hasn't
    // reached "connected" within the grace window. Consumed by the
    // SquawkDisconnectedBanner to surface a one-click reconnect affordance —
    // necessary on low-MEI origins (dev/staging) where Chrome's autoplay
    // policy blocks audio.play() after a reload until a user gesture lands.
    isDisconnectedForBanner,
    reconnect,
  };

  return (
    <SquawkPlayerContext.Provider value={value}>
      {children}

      {/* Hidden audio elements - always mounted, never unmount */}
      <audio
        id="webrtc-audio"
        autoPlay
        ref={rtcElRef}
        style={{ display: 'none' }}
      />

      <video
        key={mseElementVersion}
        id="flussonic-mse-video"
        ref={mseElRef}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          top: '-10000px',
          left: '-10000px',
          zIndex: -10,
        }}
      />
      <audio
        id="mse-background-keepalive"
        ref={mseKeepAliveElRef}
        style={{ display: 'none' }}
      />

      <audio
        id="shoutcast-audio"
        ref={shoutcastElRef}
        style={{ display: 'none' }}
      />

      <audio
        id="demo-audio"
        ref={demoElRef}
        style={{ display: 'none' }}
      />
    </SquawkPlayerContext.Provider>
  );
};

/**
 * Hook to access squawk player context
 */
// eslint-disable-next-line react-refresh/only-export-components -- context hook intentionally co-located with its Provider; dev-only fast-refresh constraint
export const useSquawkPlayer = () => {
  const context = useContext(SquawkPlayerContext);
  if (!context) {
    throw new Error("useSquawkPlayer must be used within SquawkPlayerProvider");
  }
  return context;
};
